import { FC, useEffect, useMemo } from 'react';
import { Tag } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { ColumnDef } from '@tanstack/react-table';
import { useAtom, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { FormattedCount } from 'components/formatted-count';
import { parseMomentUtc, getLocalTimezoneAbbreviation } from 'utils/dates';
import {
  selectedBatchIdAtom,
  selectedSubmissionIdAtom,
  submissionDocumentsAtom,
  batchSubmissionsAtom,
  documentFilterAtom,
} from '../atoms';
import {
  extractionStatusColors,
  submissionStatusColors,
  type ProvisioningDocument,
} from '../types';
import styles from '../page.module.scss';

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '—';
  return `${parseMomentUtc(value)
    .local()
    .format('MM/DD/YYYY h:mm:ss A')} ${getLocalTimezoneAbbreviation()}`;
};

export const SubmissionDetailPage: FC = () => {
  const { id, submissionId } = useParams<{
    id: string;
    submissionId: string;
  }>();
  const batchId = parseInt(id, 10);
  const subId = parseInt(submissionId, 10);

  const setSelectedBatchId = useSetAtom(selectedBatchIdAtom);
  const setSelectedSubmissionId = useSetAtom(selectedSubmissionIdAtom);
  useEffect(() => {
    setSelectedBatchId(batchId);
    setSelectedSubmissionId(subId);
    return () => {
      setSelectedSubmissionId(null);
    };
  }, [batchId, subId, setSelectedBatchId, setSelectedSubmissionId]);

  const [docsLoadable] = useAtom(submissionDocumentsAtom);
  const [submissionsLoadable] = useAtom(batchSubmissionsAtom);
  const [docFilters, setDocFilters] = useAtom(documentFilterAtom);

  const submission = useMemo(() => {
    if (submissionsLoadable.state !== 'hasData' || !submissionsLoadable.data)
      return null;
    return (
      submissionsLoadable.data.submissions.find((s) => s.id === subId) || null
    );
  }, [submissionsLoadable, subId]);

  const documents = useMemo(() => {
    if (docsLoadable.state !== 'hasData' || !docsLoadable.data) return [];
    return docsLoadable.data.documents || [];
  }, [docsLoadable]);

  const isLoading = docsLoadable.state === 'loading';

  const columns = useMemo<ColumnDef<ProvisioningDocument>[]>(
    () => [
      {
        id: 'documentId',
        accessorKey: 'documentId',
        header: 'Document ID',
        size: 100,
        cell: (info) => (
          <span className={styles.batchId}>#{info.getValue() as number}</span>
        ),
      },
      {
        id: 'documentTitle',
        accessorKey: 'documentTitle',
        header: 'Title',
        size: 250,
        cell: (info) =>
          (info.getValue() as string) || (
            <span className={styles.muted}>Untitled</span>
          ),
      },
      {
        id: 'extractionStatus',
        accessorKey: 'extractionStatus',
        header: 'Extraction',
        size: 120,
        cell: (info) => {
          const status = info.getValue() as string;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'completionStatus',
        accessorKey: 'completionStatus',
        header: 'Completion',
        size: 120,
        cell: (info) => {
          const status = info.getValue() as string;
          if (!status) return <span className={styles.muted}>—</span>;
          return (
            <Tag type={extractionStatusColors[status] || 'gray'} size="sm">
              {status}
            </Tag>
          );
        },
      },
      {
        id: 'entitiesCreated',
        accessorKey: 'entitiesCreated',
        header: 'Entities',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'locationsCreated',
        accessorKey: 'locationsCreated',
        header: 'Locations',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'addressesCreated',
        accessorKey: 'addressesCreated',
        header: 'Addresses',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'capacitiesCreated',
        accessorKey: 'capacitiesCreated',
        header: 'Capacities',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'citationsCreated',
        accessorKey: 'citationsCreated',
        header: 'Citations',
        size: 80,
        cell: (info) => <FormattedCount value={info.getValue() as number} />,
      },
      {
        id: 'errorMessage',
        accessorKey: 'errorMessage',
        header: 'Error',
        size: 200,
        cell: (info) => {
          const msg = info.getValue() as string;
          if (!msg) return <span className={styles.muted}>—</span>;
          return (
            <span className={styles.errorText} title={msg}>
              {msg}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        accessorKey: 'startedAt',
        header: 'Created At',
        size: 150,
        meta: { type: 'datetime' },
      },
    ],
    []
  );

  const breadcrumbs: [string, string][] = [
    ['/admin/provisioning_batches', 'Provisioning'],
    [`/admin/provisioning_batches/${batchId}`, `Provision: ${batchId}`],
  ];

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={`Submission #${subId}`}
        actions={null}
        titleDetail={
          submission ? (
            <Tag
              type={submissionStatusColors[submission.status] || 'gray'}
              size="sm"
            >
              {submission.status}
            </Tag>
          ) : null
        }
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Documents in this batch submission.
          {submission && (
            <>
              {' '}
              Provider Batch ID:{' '}
              <strong>{submission.providerBatchId || 'Pending'}</strong>.
            </>
          )}
        </div>
      </div>

      {submission && (
        <StatsBar
          stats={[
            {
              label: 'Documents',
              value: submission.documentCount,
              tagType: 'gray',
            },
            {
              label: 'Completed',
              value: submission.completedCount,
              tagType: 'green',
            },
            {
              label: 'Failed',
              value: submission.failedCount,
              tagType: 'red',
            },
          ]}
        />
      )}

      {submission && (
        <div className={styles.detailMeta}>
          <span>
            <strong className={styles.detailMetaLabel}>Submitted:</strong>{' '}
            {formatDateTime(submission.submittedAt)}
          </span>
          <span>
            <strong className={styles.detailMetaLabel}>Completed:</strong>{' '}
            {formatDateTime(submission.completedAt)}
          </span>
          <span>
            <strong className={styles.detailMetaLabel}>Progress:</strong>{' '}
            {submission.progressPercentage}%
          </span>
        </div>
      )}

      <div className={styles.tableContainer}>
        <DataTable
          data={documents}
          columns={columns}
          getRowId={(row) => String(row.documentId)}
          isLoading={isLoading}
          persistenceKey="provisioning-documents-table"
          initialTableState={{
            sortColumn: 'createdAt',
            sortDirection: 'desc',
          }}
          config={{
            columnResizing: true,
            columnReordering: true,
            columnPinning: true,
            sorting: true,
            columnVisibility: true,
            globalSearch: true,
            columnFilters: false,
          }}
        />

        <Pagination
          page={docFilters.page}
          pageSize={docFilters.perPage}
          pageSizes={[25, 50, 100]}
          totalItems={
            docsLoadable.state === 'hasData'
              ? (docsLoadable.data?.pagination.totalCount ?? 0)
              : 0
          }
          onChange={({
            page,
            pageSize,
          }: {
            page: number;
            pageSize: number;
          }) => {
            setDocFilters({ page, perPage: pageSize });
          }}
        />
      </div>
    </>
  );
};
